// The cruise view in one factory: stage, flight, ship, camera, verbs, HUD and
// its frame blocks. The game layer reaches it only through the seams in its deps.
import * as THREE from 'three';
import type { AtlasGroundGame } from '../atlas/groundGame';
import { EXPLORE_GROUND } from '../atlas/groundGame.explore';
import type { FrameCtx } from '../frame';
import type { Engagement } from '../game/engagement';
import { createHudButtons } from '../hudButtons';
import { toggleHudPanel } from '../hudMinimize';
import { createInput } from '../input';
import { openMenuTab } from '../menu';
import { createPerfPanel } from '../perfPanel';
import { type PilotTurnSide } from '../pilotPanel';
import { createBodyLabels } from '../scene/bodyLabels';
import { createPostFx } from '../scene/postFx';
import { createBodyPlaces, type BodyPlaces } from '../sim/bodies';
import { noDriveLimits, type DriveLimitsFn } from '../sim/driveLimits';
import {
  aimAlongNose, flightGravity, flightNose, flightPotential, flightSpeed,
  type FlightAxes, type FlightQuat, type FlightVec,
} from '../sim/flight';
import {
  landLabelUnlatched, preHandoff,
  type AtlasCameraHandle, type AtlasWorldHandle, type LandTarget,
} from '../sim/landVerb';
import { DEFAULT_SHIP_ORBIT, shipOrbitById, shipOrbitElements } from '../sim/orbit';
import { type PathReading } from '../sim/pathRecorder';
import type { PlayerVitalsHandle } from '../sim/playerVitals';
import { createSimPosition, orbitalBasis, type Basis } from '../sim/position';
import { eraForYear, PLAYER_PROPULSION_CLASS, thrustRates } from '../sim/thrust';
import { gameState, holdFlight, setAssistOn } from '../state/gameState';
import { loadBodies, bodyById, ensureOsculating } from '../world/bodies';
import { earthDateMsFromT, gameClock, isoUtcFromT, MISSION_ANCHOR_T, tFromIsoUtc } from '../world/clock';
import { heliocentricOrbit, stateAt } from '../world/ephemeris';
import { type WorldVec } from '../world/frame';
import type { BodyRow } from '../world/types';
import { createCameraRig, type CameraPinDiagnostics } from './cameraRig';
import { createCruiseSim } from './createCruiseSim';
import type { CruiseDrive } from './drive';
import {
  createFlightField, type ContactReading, type GroundSeatReading,
} from './flightField';
import {
  createPilotTarget, type ParkBetweenResult, type ParkResult,
} from './pilotTarget';
import { createPlayerShip } from './playerShip';
import { createProgradeView } from './progradeView';
import { createCruiseStage, type CruiseStageDeps } from './stage';
import { createCruiseMenuSystems, createCruiseViewSystems } from './systems';
import {
  createCruisePilotPanel, createCruiseLandVerb, createCruiseTrajectory, createCruiseWarp,
} from './verbs';
import { urlFlag, urlParam } from '../urlFlags';
import { TEST_SWITCHES } from '../urlFlags.ts';

// Flags the cruise view reads; the guards let a test import this module with no page.
const CLOCK_CONST = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('clockconst') === '1';
// `?fightheld=1`: a fight holds the registry at the epoch it opened on and its
// bubble where it was seeded, as it did, so the first frame out of it carries
// the ship by its parent's whole travel over the fight. `save-round-trip.mjs
// --leg fight-end --inject fight-held` opens it; DEV only.
const FIGHT_HELD = TEST_SWITCHES && typeof location !== 'undefined'
  && new URLSearchParams(location.search).get('fightheld') === '1';
const ORBIT_ID = (typeof location !== 'undefined' ? new URLSearchParams(location.search).get('shiporbit') : null)
  ?? DEFAULT_SHIP_ORBIT;
// `?simorrery=1` puts the retired stand-in orrery back, on purpose: one flat
// `MeshBasicMaterial` sphere per row at its own heliocentric place, over the
// Atlas's own globe. `tools/preview/descent-dojo.mjs --world --inject
// sim-orrery` needs the first drawn cruise frame's dominant body to carry a
// second, non-imagery disc at its own place - nothing a player reaches sets it.
const SIM_ORRERY = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('simorrery') === '1';
const IDENTITY_BASIS: Basis = { x: { x: 1, y: 0, z: 0 }, y: { x: 0, y: 1, z: 0 },
  z: { x: 0, y: 0, z: 1 } };
// ---------- Hand flight ----------
// In cruise the player flies by hand: held axes on all three, a held throttle
// along the nose, and nothing slowing the ship down (`src/sim/flight.ts`). The
// state is HELIOCENTRIC - the frame `simPos` holds the doubles in - so a
// re-seed of the bubble's own basis leaves the coast pointing where it was.
// A fight keeps its own 2D model inside the bubble and this does not step.
// What the drive delivers is canon's, per hull class and per era: the sibling
// `MillionOnMars/4xciv` repo, `data-lake/energy/06-ship-engines-combat.md`
// section 3.2. Canon gives two rates per row, and the one every burn in this
// build flies is the SUSTAINED one - the hand throttle, the autopilot, the
// burn planner and the assist all hold their thrust for minutes of simulated
// time, which is what that column means. The momentary burst is carried
// beside it; nothing reaches for it yet, since there is no burst control and
// no budget to trade one against.
const CRUISE_DRIVE = thrustRates(
  PLAYER_PROPULSION_CLASS,
  eraForYear(new Date(earthDateMsFromT(MISSION_ANCHOR_T)).getUTCFullYear()),
);
const CRUISE_TURN_RATE_RAD_S = 0.8;
// `?drag=1` puts a drag term back into the step: the broken-on-purpose variant
// `tools/preview/descent-dojo.mjs --flight --inject drag` opens, where the
// coast loses speed and the arm must fail.
const FLIGHT_DRAG = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('drag') === '1';
const FLIGHT_DRAG_PER_S = 0.05;
// ?pickunchecked=1: simFlight.pickTarget takes an id the list lacks (autowarp-check --leg target's control).
const PICK_UNCHECKED = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('pickunchecked') === '1';
// `?bootrest=1` leaves the boot flight velocity at zero, the way it was before
// the scenario orbit's own velocity reached the flight state: the broken-on-
// purpose variant `tools/preview/descent-dojo.mjs --warp --inject boot-rest`
// opens, where a warped coast falls straight into the body the ship is meant
// to be orbiting. Nothing a player reaches sets it.
const BOOT_REST = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('bootrest') === '1';
// `?bootheliobasis=1` puts the pre-fix attitude basis back: built from the
// COMPOSED heliocentric velocity rather than the parent-relative half the
// flight state actually flies with, so the boot nose lands tens of degrees
// off the ship's own velocity instead of the small residual the fix leaves.
// The control `tools/preview/descent-dojo.mjs --flight --boot` opens against
// its own boot-angle bar. Nothing a player reaches sets it.
const BOOT_HELIO_BASIS = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('bootheliobasis') === '1';
// `?seedbeforesets=1` seeds the scenario's ship before its parent's own
// osculating sets land, the pre-fix order that left a phobos-low ship 1,220 km
// behind the moon. `tools/preview/land-body-check.mjs --inject seed-before-sets`
// opens it; nothing a player reaches sets it.
const SEED_BEFORE_SETS = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('seedbeforesets') === '1';
// `?nocarry=1` leaves a ship seeded off its parent's secular fit where it stood
// when the sets land; `?seedbeforesets=1` implies it. The walkaround arm's
// sets-reject, sets-network and sets-missing controls open it; no player does.
const NO_SEED_CARRY = SEED_BEFORE_SETS || TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('nocarry') === '1';
// Sim seconds one integration substep may take once a body is pulling. Held
// as a number rather than derived from the frame so neither a slow frame nor
// the warp below can lengthen a substep: they buy more of them instead. At 2 s
// a 300 km Mars orbit gets about 3,400 substeps a revolution.
const FLIGHT_MAX_SUBSTEP_S = 2;
// `?bodiespinned=1` puts back the registry that was placed once at the boot
// epoch and never again while the Atlas drew the same bodies at the live
// clock: the broken-on-purpose variant
// `tools/preview/descent-dojo.mjs --flight --separation --inject bodies-pinned`
// opens it. Nothing a player reaches sets it.
const BODIES_PINNED = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('bodiespinned') === '1';
// `?rewindcarry=1` leaves the registry at the epoch a load replaced, so the
// first frame after a restart or a restore carries the ship across the rewind:
// the broken-on-purpose variant
// `tools/preview/save-round-trip.mjs --leg restart --inject rewind-carry` opens.
// DEV only, so no build carries it.
const REWIND_CARRY = TEST_SWITCHES && typeof location !== 'undefined'
  && new URLSearchParams(location.search).get('rewindcarry') === '1';
// `?restartanchor=1` puts back the restart a scenario used to run: the ship
// dropped at the bubble's old zero at rest, and the clock rewound to the
// mission anchor. The broken-on-purpose variant
// `tools/preview/save-round-trip.mjs --leg restart --inject restart-anchor`
// opens, and the only restart that still rewinds, which `--inject
// rewind-carry` needs to read a carry across the rewind at all. DEV only.
export const RESTART_ANCHOR = TEST_SWITCHES && typeof location !== 'undefined'
  && new URLSearchParams(location.search).get('restartanchor') === '1';
// `?restartnoseed=1` opens a scenario without seeding its bubble at all: the
// ship keeps its place but the layout is laid about a zero it has left. The
// broken-on-purpose variant `save-round-trip.mjs --leg restart --inject
// restart-noseed` opens, the layout bar's own control. DEV only.
const RESTART_NOSEED = TEST_SWITCHES && typeof location !== 'undefined'
  && new URLSearchParams(location.search).get('restartnoseed') === '1';
// `?resumedrop=steps` restores a saved fight with its clock at zero,
// `?resumedrop=ordinals` re-attaches its captains on the running count,
// `?resumedrop=lock` drops a restored rock lock, `?resumedrop=traffic-clock`
// skips handing the traffic field's own clock back, `?resumedrop=contact`
// saves a captain's shot at a ship as `ship` rather than `contact`,
// `?resumedrop=teardown-first` tears the running fight down before every
// refusal is checked, the way `restore()` used to,
// `?resumedrop=rollback-refuse` refuses a load outright when the running
// fight cannot be rolled back rather than going ahead without a snapshot, the
// way `restore()` used to, `?resumedrop=rollback-guard` takes a rollback
// snapshot the running fight cannot rebuild from anyway, instead of going
// ahead with no snapshot, the way `restore()` used to,
// `?resumedrop=shot-throw` throws naming a shot whose target has died instead
// of saving it as `gone`, the way `nameShotTarget` used to,
// `?resumedrop=inspected` skips restoring the inspect panel's target, the way
// a load used to leave it, and `?resumedrop=no-teardown` skips tearing a
// fight `overlayFight` has already mounted down before the catch resets to
// the clean state, leaving it standing over that reset the way `restore()`'s
// catch used to: the broken-on-purpose variants
// `tools/preview/combat-replay.mjs --leg resume`, `--leg mismatch`,
// `--leg authored` and `--leg shot` open. DEV only, so no build carries it.
export const RESUME_DROP = TEST_SWITCHES && typeof location !== 'undefined'
  ? new URLSearchParams(location.search).get('resumedrop') : null;
// `?flightcarryoldparent=1` puts back the order where the frame's parent was
// picked AFTER the carry, so a handoff frame is carried on the parent it is
// leaving and flown in the one it is entering. The broken-on-purpose variant
// `--warp --inject approach-carry-old-parent` opens it; no player reaches it.
const FLIGHT_CARRY_OLD_PARENT = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search)
  .get('flightcarryoldparent') === '1';
// `?flightcentresheld=1` holds every pull centre still for a whole frame again
// instead of moving it across the frame's spans, which at the top warp level is
// 8,640 simulated seconds of a body's own travel ignored. The broken-on-purpose
// variant `--warp --inject approach-centres-held` opens it; no player reaches it.
const FLIGHT_CENTRES_HELD = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search)
  .get('flightcentresheld') === '1';
// `?flighthelioframe=1` integrates the ship in the heliocentric frame with the
// registry's own bodies moving under it, which is the wrong frame to write
// this in: the frame the orbit is about is no longer the frame
// the state is in, so a parent travelling ten orbital radii between two samples
// of the ephemeris takes the orbit with it. `--flight --orbit --inject
// helio-frame` opens it. Nothing a player reaches sets it.
const FLIGHT_HELIO_FRAME = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('flighthelioframe') === '1';
// Enough of them that the top warp level still gets the substep above. The
// frame's simulated seconds are `rate * dt`, the clock's top rate is 86,400
// and `dt` is clamped at 0.1 real seconds, so the worst frame asks for 8,640
// sim s: 4,320 substeps of 2 s. The cap was 64, which turned a day a second
// into 135 s substeps and let a parked orbit decay while the player waited
// for a window. 4,320 of these is a few hundred thousand flops a frame.
const FLIGHT_MAX_SUBSTEPS = 4320;
// ---------- Flight assist ----------
// On by default and switchable off from the action bar: while it is on,
// lateral thrusters bring the ship's own motion round onto the last aim the
// player commanded, out of the same delta-v budget the main engine spends
// (`applyAssist` in src/sim/flight.ts). The aim is a direction held beside the
// attitude, not the bow, so the bow can go on framing a body at no cost. A
// held axis is not a precondition either - an aim dragged over and let go goes
// on coming round until it is there. Switched off, the flight underneath is
// the Newtonian one it has always been. The switch itself lives in
// `gameState().ship.flight.assistOn`; `setAssistOn` is its one mutator.
// `?assistfree=1` snaps the velocity onto the nose for free: the
// broken-on-purpose variant `tools/preview/descent-dojo.mjs --assist --inject
// assist-free` opens, where both the rate and the cost bars must fail.
const ASSIST_FREE = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('assistfree') === '1';
// `?assistrealonly=1` puts back the gate that confined the assist to real
// time, where you could aim under warp but never change where you were going:
// the broken-on-purpose variant `--assist --inject assist-realonly` opens.
const ASSIST_REAL_ONLY = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search)
  .get('assistrealonly') === '1';
// `?assistrealsecs=1` lets the assist act under warp but over the frame's REAL
// seconds, so the turn comes out weaker than the clock by exactly the warp
// factor: the broken-on-purpose variant `--assist --inject assist-realsecs`
// opens. Nothing a player reaches sets either.
const ASSIST_REAL_SECS = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search)
  .get('assistrealsecs') === '1';
// `?assistnoresync=1` restores the pre-fix OFF->ON edge, where switching the
// assist back on never closes a gap it inherited unless a turn axis is held:
// the broken-on-purpose variant `--assist --inject assist-noresync` opens.
// Nothing a player reaches sets it.
const ASSIST_NORESYNC = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('assistnoresync') === '1';
// `?assistheldonly=1` gates the assist on a held axis again, so an aim dragged
// over and let go freezes where the drag left it: the broken-on-purpose
// variant `--assist --inject assist-held-only` opens. `?assistnoresync=1`
// implies it, since the pre-fix OFF->ON edge that control restores WAS this
// gate with nothing armed. Nothing a player reaches sets either.
const ASSIST_HELD_ONLY = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('assistheldonly') === '1'
  || ASSIST_NORESYNC;
// `?assistaimfromnose=1` takes the aim off whatever the bow is pointing at
// when the assist is switched on, rather than leaving the last aim the player
// commanded: at boot that is the framing bow, 90 degrees off the track, so one
// off-and-on click fires a deorbit burn. The broken-on-purpose variant
// `--flight --boot --inject assist-aim-from-nose` opens it. Nothing a player
// reaches sets it.
const ASSIST_AIM_FROM_NOSE = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search)
  .get('assistaimfromnose') === '1';
// `?assistkeepaim=1` keeps the commanded aim across a frame handoff instead of
// dropping it, which is what `rebaseFlightFrame` below used to do. The control
// `--assist --inject assist-keep-aim` opens it; nothing a player reaches does.
const ASSIST_KEEP_AIM = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('assistkeepaim') === '1';
// `?autopilotkeepaim=1` hands a trip back with the aim the autopilot's own
// turns left on the state instead of clearing it, which is what an arrival did
// before: the control `--moon-go --hold --inject arrival-keeps-aim` opens it,
// and nothing a player reaches does.
const AUTOPILOT_KEEP_AIM = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search)
  .get('autopilotkeepaim') === '1';
// `?assistfrozenaim=1` freezes the commanded aim in the flight frame again
// instead of letting it ride the turn a pull puts on the velocity, so the
// assist buys gravity's own turning back every substep and a parked orbit
// sinks under it at every rate: the broken-on-purpose variant
// `--flight --orbit --assist-warp --inject assist-frozen-aim` opens. Nothing a
// player reaches sets it.
const ASSIST_FROZEN_AIM = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search)
  .get('assistfrozenaim') === '1';
// Real seconds one frame may bill the sim for, whatever it took. A frame's sim
// span is this times the warp rate, so anything pricing a frame uses this.
export const MAX_REAL_DT_S = 0.1;

// `?assistnofloor=1` puts the assist's gap guards back to a test against zero,
// so a closed gap is still a gap and the velocity is turned by float residue
// every substep: the broken-on-purpose variant
// `--matched --inject assist-no-floor` opens. Nothing a player reaches sets it.
const ASSIST_NO_FLOOR = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('assistnofloor') === '1';
// `?assistpushnose=1` solves the drawn lateral push from the NOSE off the
// velocity again rather than from the commanded aim the assist actually turns
// toward: the broken-on-purpose variant `--matched --inject assist-push-nose`
// opens. Nothing a player reaches sets it.
const ASSIST_PUSH_NOSE = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('assistpushnose') === '1';
// `?liftoffinertial=1` drops the tangential velocity a resting offset carried
// under the body's own spin at liftoff instead of adding it back: the
// broken-on-purpose variant `grounded-view-check.mjs --inject liftoff-inertial`
// opens. Nothing a player reaches sets it.
const DROP_LIFTOFF_SPIN = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('liftoffinertial') === '1';
// `?headinginertial=1` holds a resting ship's orientation inertial instead of
// turning it with the ground: the broken-on-purpose variant
// `grounded-view-check.mjs --inject heading-inertial` opens. Nothing a player
// reaches sets it.
const INERTIAL_HEADING = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('headinginertial') === '1';
// `?nogravity=1` switches every pull off: the broken-on-purpose variant
// `tools/preview/descent-dojo.mjs --flight --orbit --inject no-gravity` opens,
// where a ship parked at orbital speed flies straight out past the radius its
// body stops being the strongest pull at. Nothing a player reaches sets it.
const FLIGHT_GRAVITY = typeof location === 'undefined' || !(TEST_SWITCHES && urlFlag('nogravity'));
// `?onebody=1` puts the pre-fix selection back on purpose: only the primary
// and the body pulling hardest, the way this file worked before every body
// pulled. `tools/preview/descent-dojo.mjs --flight --pulls --inject one-body`
// opens it to show the check catching the bodies it drops. Nothing a player
// reaches sets it.
const FLIGHT_ONE_BODY = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('onebody') === '1';
// `?assistnocapnotice=1` keeps the panel quiet while the assist's own cap
// binds, which is the pre-#347 panel: it spent, fell behind the nose, and said
// nothing. The broken-on-purpose variant `--inject assist-no-cap-notice` opens
// it. Nothing a player reaches sets it. Read again inside `src/cruise/pilotTarget.ts`,
// which carries its own copy for the pilot readout; this one is for the
// `scene.userData.simFlight` debug object below, which is not part of that move.
const ASSIST_NO_CAP_NOTICE = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('assistnocapnotice') === '1';
// `?maydaysimsecs=1` puts the reactor clock back on warped sim seconds, where
// 1 HR/S spends the whole 47 minutes inside the first real minute. The
// broken-on-purpose variant `--inject mayday-sim-secs` opens it. Nothing a
// player reaches sets it.
export const MAYDAY_SIM_SECONDS = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('maydaysimsecs') === '1';
// `?markerstale=1` holds the ship's parent-relative state still while the
// Solar System view has the screen, which is the feed that only updated on a
// close and reopen: the marker then rides its parent as a fixed offset and
// never moves against the body it is orbiting. The broken-on-purpose variant
// `--viewwarp --inject marker-stale` opens it, where the animation reading
// must fail. Nothing a player reaches sets it.
const MARKER_STALE = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('markerstale') === '1';
// `?linestale=1` stops rebuilding the predicted path while the Solar System
// view has the screen, which leaves the line anchored where the ship was when
// the map opened while the bodies walk out from under it. The
// broken-on-purpose variant `--viewwarp --inject line-stale` opens it, where
// the near end's own reading must fail. Nothing a player reaches sets it.
const LINE_STALE = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('linestale') === '1';
// `?assistbootarmed=1` arms the assist at the boot seed, so it inherits the
// 90 degree gap between a bow pointed at the orbited body and the velocity a
// circular orbit carries, and burns to close it with nobody at the controls.
// The broken-on-purpose variant `--flight --boot --inject boot-assist-armed`
// opens it. Nothing a player reaches sets it.
const ASSIST_BOOT_ARMED = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('assistbootarmed') === '1';
// `?navalthrottlebar=1` maps a throttle click across the ship class's naval
// speed range again while the bar is drawn on the cruise scale, which put every
// click past the first few pixels on an end stop: the broken-on-purpose variant
// `tools/preview/layout-check.mjs --throttle --inject throttle-naval` opens it.
export const NAVAL_THROTTLE_BAR = TEST_SWITCHES && typeof location !== 'undefined' && new URLSearchParams(location.search).get('navalthrottlebar') === '1';

type Phase = { before(c: FrameCtx): void; after(c: FrameCtx): void };
const NO_PHASE: Phase = { before: () => {}, after: () => {} };

type HeatSeam = Pick<PlayerVitalsHandle, 'redLine' | 'heatStepInput'>;
/** A fresh vitals' heat, before the first tick: warp paints at construction and reads it. */
const COLD_HEAT: HeatSeam = { redLine: false, heatStepInput: () => ({ throttle: 0, deployFraction: 0, pastRedLine: false }) };

/** What the cruise view reads from the game around it; every seam defaults to no game. */
export interface CruiseViewDeps extends CruiseStageDeps {
  app: HTMLElement;
  clock: THREE.Clock;
  /** What the Atlas ground offers at a landing. */
  ground?: AtlasGroundGame;
  /** SHIP_SPECS key of the player's hull (src/cruise/playerShip.ts). Default
   *  the Pup, which is explore's. */
  playerSpec?: string;
  engagement?: () => Engagement | null;
  /** The player's vitals once built, null before. */
  heat?: () => HeatSeam | null;
  driveLimits?: DriveLimitsFn;
  deployForTrip?: () => void;
  /** Share of full thrust a trip GO starts from here flies its burns at, which
   *  the delta-v map prices them on. */
  tripThrustShare?: () => number;
  beforeFly?: (dt: number) => void;
  fightCamera?: (realDt: number) => boolean;
  rebaseInFight?: () => void;
  sceneMisc?: Phase;
  hud?: Phase;
  /** False when the scenario aims its own bow, so the boot seed must not swing it. */
  bowFramed?: () => boolean;
  setBootHeading?: (heading: number) => void;
  traffic?: { fieldRadius: number; ships(): Array<{ name: string; canonical: boolean; range: number }> };
}

/** The cruise view: stage, flight, ship, camera, verbs, HUD and its frame blocks. */
export function createCruiseView(deps: CruiseViewDeps) {
const {
  renderer, contextLoss, app, clock, ground = EXPLORE_GROUND, playerSpec = 'pup',
  engagement = () => null, heat = () => null, driveLimits = noDriveLimits,
  deployForTrip = () => {}, tripThrustShare, beforeFly = () => {},
  fightCamera = () => false, rebaseInFight = () => {},
  sceneMisc = NO_PHASE, hud: hudSeam = NO_PHASE,
  bowFramed = () => true, setBootHeading = () => {}, traffic = { fieldRadius: 0, ships: () => [] },
} = deps;
const game = { get engagement(): Engagement | null { return engagement(); } };
const inFight = (): boolean => game.engagement !== null;
const vitals: HeatSeam = {
  get redLine() { return (heat() ?? COLD_HEAT).redLine; },
  heatStepInput: () => (heat() ?? COLD_HEAT).heatStepInput(),
};
let cruiseDriveBuilt: CruiseDrive | null = null;

// The sim's scene mount and camera, the world frame, the bubble and its
// lights (src/cruise/stage.ts), built on the renderer and context-loss gate
// createPageRenderer(app, ...) already built above.
const {
  world, scene, simWorld, simRoot, camera, worldFrame, bubble, simLights,
} = createCruiseStage({ renderer, contextLoss });

// Seeded at the frame origin and re-seeded onto the scenario's own orbit as
// soon as the registry resolves (`seedShipOrbit` below). The fight's own metres
// are unchanged by that: a seed puts the ship back at the bubble's zero, which
// is where every scripted position in a scenario is measured from.
const simPos = createSimPosition({ start: { x: 0, y: 0, z: 0 }, basis: IDENTITY_BASIS });

const _flightQuat = new THREE.Quaternion();

/** Sim seconds the flight step has integrated. The clock a period is read against. */
let flightSimSeconds = 0;

// Gravity's pulling set, the flight frame, the frame-parent carry, ground
// contact, the path recorder and the launch/land verbs (`src/cruise/flightField.ts`).
const flightField = createFlightField({
  simPos, bubble, atlasWorld, syncShipTransform,
  assistKeepAim: ASSIST_KEEP_AIM,
  get ship() { return ship; },
  get warp() { return warp; },
  get landVerb() { return landVerb; },
  get pilot() { return pilot; },
  get game() { return game; },
  turnRate: CRUISE_TURN_RATE_RAD_S,
  accel: CRUISE_DRIVE.sustainedMps2,
  dragOn: FLIGHT_DRAG,
  dragPerS: FLIGHT_DRAG_PER_S,
  maxSubstepS: FLIGHT_MAX_SUBSTEP_S,
  maxSubsteps: FLIGHT_MAX_SUBSTEPS,
  assistFree: ASSIST_FREE,
  assistHeldOnly: ASSIST_HELD_ONLY,
  assistFrozenAim: ASSIST_FROZEN_AIM,
  assistNoFloor: ASSIST_NO_FLOOR,
  dropLiftoffSpin: DROP_LIFTOFF_SPIN,
  inertialHeading: INERTIAL_HEADING,
  assistRealOnly: ASSIST_REAL_ONLY,
  assistRealSecs: ASSIST_REAL_SECS,
  gravityOn: FLIGHT_GRAVITY,
  oneBody: FLIGHT_ONE_BODY,
  helioFrame: FLIGHT_HELIO_FRAME,
  centresHeld: FLIGHT_CENTRES_HELD,
  fightHeld: FIGHT_HELD,
  bodiesPinned: BODIES_PINNED,
  rewindCarry: REWIND_CARRY,
  live: {
    get simBodies() { return simBodies; },
    get bodyRows() { return bodyRows; },
    get bootJd() { return bootJd; },
    get seedJd() { return seedJd; },
    set seedJd(v: number) { seedJd = v; },
    get seedJdPrev() { return seedJdPrev; },
    set seedJdPrev(v: number) { seedJdPrev = v; },
    get flightSimSeconds() { return flightSimSeconds; },
  },
});
const {
  flightConfig, flight, gravityField, _frameCtx,
  assistSeconds, assistLive,
  stepFlightFrame, advanceSimBodies, frameParentStep, rideFightFrame, placeFrameEpoch,
  adoptFlightFrame, rebaseFlightFrame, contactRadiusM, noteStopLateSag, noteGroundContact,
  pathRecorder, pathValues, notePathRow, hullReachM, seatShipOnDrawnSurface,
  groundSeatReading, noteContactReading, groundUnderReading, attemptLaunch,
  shipWorldAt, shipWorldVelocity, slotSurfaceRadiusM, slotSurfaceDir: _slotSurfaceDir,
} = flightField;
// The destructure below keeps every array and struct name main.ts already used.
const {
  field: _field, ground: _ground, pulls: _pulls, pullIds: _pullIds,
  pullRadiiM: _pullRadii, pullRows: _pullRows, pullPeriodS: _pullPeriodS,
  pullParentIdx: _pullParentIdx,
  update: updateFlightField, readPulls: readFlightPulls, refill: refillFlightField,
  pickDominant: pickDominantPull, holdRadiusM: bodyHoldRadiusM,
  meanMotionRadS: bodyMeanMotionRadS, sampleCentre: sampleFlightCentre,
  frameAccelAt: fillFrameAccel,
} = gravityField;

// `?targetdefaultnone=1` puts the pre-fix boot pick back: NONE rather than
// Mars (or the dominant pull, if the registry ever ships without a Mars
// row). The broken-on-purpose variant `--target --inject target-default-none`
// opens it, where the boot-default bar must fail. Nothing a player reaches
// sets it.
const TARGET_DEFAULT_NONE = TEST_SWITCHES && typeof location !== 'undefined'
  && new URLSearchParams(location.search).get('targetdefaultnone') === '1';

/**
 * The boot pick for the pilot panel's target select: Mars, so the guidance
 * rows are live the moment the sim opens. Falls back to whatever is pulling
 * hardest at the seeded orbit if the registry ever ships without a Mars row,
 * and to NONE if nothing is pulling either - a missing row must never leave
 * the select broken or empty of a choice. `seedShipOrbit` has already placed
 * the ship and `simBodies` by the time this runs. `?targetdefaultnone=1`
 * keeps the pre-fix NONE default, for `--inject target-default-none`.
 */
function defaultFlightTargetId(rows: readonly BodyRow[]): string | null {
  if (TARGET_DEFAULT_NONE) return null;
  if (bodyById(rows, 'mars') !== undefined) return 'mars';
  readFlightPulls();
  const bestIdx = pickDominantPull(_field.at);
  return bestIdx < 0 ? null : (_pullIds[bestIdx] ?? null);
}

/** The substep count and length the last flight step ran at, for the check. */
let flightLastSubsteps = 0;
let flightLastSubstepS = 0;

// Snapshot taken once, at the instant `seedShipOrbit` adopts the boot
// attitude - before any tick has run and so before the assist has spent
// anything. `tools/preview/descent-dojo.mjs --flight --boot` reads this
// rather than polling after the page settles, since by then the assist has
// already had up to a second or two of real time to act and close the gap
// this is meant to catch fresh.
let bootNoseGapDeg: number | null = null;
// Degrees between the boot nose and the true direction to the focused body,
// taken at the same instant. OWNER DECISION 2 wants the bow pointed at that
// body; this is what actually reads whether the boot attitude basis achieved
// it, since `bootNoseGapDeg` above is dominated by circular-orbit geometry
// (nose-at-body is close to nose-perpendicular-to-velocity) and cannot tell a
// consistent basis from an inconsistent one on its own.
let bootNoseBodyGapDeg: number | null = null;

// The picked row lives in `gameState().ship.flight.flightTargetId`, or null
// for none; `setFlightTargetId` is its one mutator. The pick, its guidance,
// the pilot panel's own readout and the park verbs all live in
// `src/cruise/pilotTarget.ts` now (`createPilotTarget`), reached through
// `pilot` below, built once `cruiseDrive` exists.
/**
 * Snap the nose and the commanded aim onto minus the velocity. A check's own
 * control behind `simFlight.faceRetrograde`: a player flies this turn by hand,
 * onto the purple glyph the marker pair already draws there, and the turn's
 * own rate is `--flight --hand`'s ground rather than this.
 */
function faceRetrogradeNow(): boolean {
  const v = flight.velocity;
  const speed = Math.hypot(v.x, v.y, v.z);
  if (!(speed > 0)) return false;
  _flightQuat.setFromUnitVectors(new THREE.Vector3(0, 0, -1),
    new THREE.Vector3(-v.x / speed, -v.y / speed, -v.z / speed));
  flight.orientation.x = _flightQuat.x;
  flight.orientation.y = _flightQuat.y;
  flight.orientation.z = _flightQuat.z;
  flight.orientation.w = _flightQuat.w;
  ship.group.quaternion.copy(_invBubble).multiply(_flightQuat);
  aimAlongNose(flight);
  return true;
}

// The chase rig, the ground stand-off camera and the body pin all now live in
// src/cruise/cameraRig.ts (`createCameraRig`); `rig` below is that handle,
// built once the ship and the bubble's inverse exist.
const _cameraWorld: WorldVec = { x: 0, y: 0, z: 0 };
const _cameraScene = new THREE.Vector3();
const _basisMat = new THREE.Matrix4();
// The registry's own bodies, built once the registry lands (`seedShipOrbit`).
let simBodies: BodyPlaces | null = null;
// The epoch the ship's own state was propagated to, and the epoch the bodies
// are drawn at. One epoch and not the live clock's: the ship stands still in
// heliocentric metres until round 11's transit verb flies it, so a live epoch
// would have Mars leave the bow at 24 km/s while the ship did not follow.
let seedJd = Number.NaN;
/** The epoch the registry was placed at on the PREVIOUS frame. */
let seedJdPrev = Number.NaN;
/** The boot epoch, which `?bodiespinned=1` holds the registry at. */
let bootJd = Number.NaN;
// The registry itself, once it lands: the land verb reads the focused row off
// it rather than off the body table's own copy.
let bodyRows: readonly BodyRow[] = [];
// Read by `tools/preview/descent-dojo.mjs --ship`, the way the Atlas hands its
// own camera over on `scene.userData.atlasCamera`.
scene.userData['simPosition'] = simPos;
// The land verb's own state, for the same arm: whether the affordance is live,
// what it refuses on, and the point and band edges a press would hand `goTo`.
scene.userData['simLand'] = {
  get enabled(): boolean { return landVerb.target().ok; },
  get reason(): string | null {
    const t = landVerb.target();
    return t.ok ? null : t.why;
  },
  get landed(): boolean { return landVerb.open; },
  target(): LandTarget | null {
    const t = landVerb.target();
    return t.ok ? t.at : null;
  },
  /**
   * How far the camera stands off the hull right now against the chase rig's
   * own distance, scene metres. The view a REFUSED hand-off leaves behind is
   * the stand-off camera, and that is the number which says so.
   * `tools/preview/grounded-handoff-check.mjs` reads it.
   */
  standOff(): { camToShipM: number; chaseM: number } {
    return {
      camToShipM: camera.position.distanceTo(ship.group.position),
      chaseM: gameState().ship.chase.dist,
    };
  },
  /** Is the pre-hand-off control armed? */
  get preHandoff(): boolean { return preHandoff(); },
};


// The clock by its UTC date. `?date=<ISO UTC>` opens the page on it, read at
// the first line the clock is used; under a boot scenario the ship orbit is
// seeded at the scenario anchor and only the clock moves. `setDate` is the same
// move once running.
function setDate(isoUtc: string): boolean {
  const t = tFromIsoUtc(isoUtc);
  if (t === null) return false;
  gameClock().setInstant(t);
  return true;
}
const DATE_PARAM = TEST_SWITCHES ? urlParam('date') : null;
if (DATE_PARAM !== null && !setDate(DATE_PARAM)) console.warn(`?date=${DATE_PARAM} is not an ISO UTC date`);
scene.userData['simClock'] = {
  setDate,
  get utc(): string { return isoUtcFromT(gameClock().t); },
  get jd(): number { return gameClock().jdTdb(); },
};

// Read and driven by `tools/preview/descent-dojo.mjs --flight`, beside the
// cruise census's handle above. `hold` and `release` take the controls off the
// keyboard so a page can fly a known burn and then coast; `clear` gives them
// back. Nothing here writes the velocity directly - only a burn moves it.
scene.userData['simFlight'] = {
  get active(): boolean { return game.engagement === null; },
  get drag(): number { return flightConfig.drag; },
  dragParam: FLIGHT_DRAG,
  // A getter, not the constant: BURN reads the live config, so a harness
  // truth taken off this stays right once the rate stops being one number.
  get accel(): number { return flightConfig.accel; },
  /** Canon's burst rate for this hull, carried beside the sustained one every burn flies. */
  momentaryAccel: CRUISE_DRIVE.momentaryMps2,
  turnRate: CRUISE_TURN_RATE_RAD_S,
  get speed(): number { return flightSpeed(flight); },
  get deltaV(): number { return flight.deltaVSpent; },
  get heldThrottle(): number { return gameState().ship.flight.heldCruiseThrottle; },
  /** The assist's own switch, and whether the clock lets it act. Read, never set. */
  get assist(): boolean { return gameState().ship.flight.assistOn; },
  get assistLive(): boolean { return assistLive(); },
  /** Is an OFF->ON catch-up currently closing an inherited gap? Read, never set. */
  get assistCatchingUp(): boolean { return flight.assistCatchUp; },
  /** Did the assist's own per-substep cap bind on the last step? Read, never set. */
  get assistCapped(): boolean { return flight.assistCapped; },
  assistNoCapNotice: ASSIST_NO_CAP_NOTICE,
  /** Has the player commanded an aim for the assist to hold? Read, never set. */
  get assistAimed(): boolean { return flight.assistAim !== null; },
  /** The commanded aim, a unit direction in the flight frame, or null. */
  assistAim(): FlightVec | null {
    return flight.assistAim === null ? null : { ...flight.assistAim };
  },
  /** True on `?assistbootarmed=1`, where the boot seed arms the assist. */
  assistBootArmed: ASSIST_BOOT_ARMED,
  /** True on `?assistaimfromnose=1`, where switching the assist on takes the
   * bow as the aim instead of leaving the commanded one alone. */
  assistAimFromNose: ASSIST_AIM_FROM_NOSE,
  assistFree: ASSIST_FREE,
  assistRealOnly: ASSIST_REAL_ONLY,
  assistRealSecs: ASSIST_REAL_SECS,
  /** True on `?bootrest=1`, where the boot orbit's own velocity is withheld. */
  bootRest: BOOT_REST,
  /** True on `?bootheliobasis=1`, where the boot attitude basis is built from
   * the composed heliocentric velocity instead of the parent-relative one. */
  bootHelioBasis: BOOT_HELIO_BASIS,
  /** Is the outward park's plane coming off its own radial `r x v`? */
  get parkRadialBasis(): boolean { return warp.parkRadialBasis; },
  /** Arm or disarm that control. A check's own switch; no player reaches it. */
  setParkRadialBasis(on: boolean): void { warp.setParkRadialBasis(on); },
  assistNoResync: ASSIST_NORESYNC,
  assistHeldOnly: ASSIST_HELD_ONLY,
  assistKeepAim: ASSIST_KEEP_AIM,
  autopilotKeepAim: AUTOPILOT_KEEP_AIM,
  assistFrozenAim: ASSIST_FROZEN_AIM,
  assistNoFloor: ASSIST_NO_FLOOR,
  assistPushNose: ASSIST_PUSH_NOSE,
  get chaseDistance(): number { return gameState().ship.chase.dist; },
  get cameraFovDeg(): number { return camera.fov; },
  get chaseOffNose(): boolean { return rig.chaseOffNose(); },
  get chaseOffDefault(): boolean { return rig.chaseOffDefault(); },
  /** The nearest and default chase distance for the hull drawn now, and its radius. */
  chaseLimits(): { min: number; defaultDist: number; hullRadius: number; max: number } { return rig.chaseLimits(); },
  /** The camera's near plane as drawn, metres. */
  get cameraNear(): number { return camera.near; },
  /** The camera pin: its switch, the row it is holding, and the controls' flags. */
  pin(): CameraPinDiagnostics { return rig.pinDiagnostics(); },
  /** Degrees between the camera's forward and the ship's nose. */
  get cameraOffNose(): number {
    const a = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    const b = new THREE.Vector3(0, 0, -1).applyQuaternion(ship.group.quaternion);
    return Math.acos(THREE.MathUtils.clamp(a.dot(b), -1, 1)) * 180 / Math.PI;
  },
  /** The drawn camera against the ship, both in the bubble's axes, with where the
   *  ship's centre lands on screen and the look-around turn still to make.
   *  `tools/preview/chase-orbit-check.mjs`'s own reading. */
  chaseView(): {
    cam: FlightQuat; ship: FlightQuat; offset: FlightVec; shipNdc: { x: number; y: number };
    fov: number; aspect: number; pendingRad: number; chaseDist: number;
  } {
    camera.updateMatrixWorld();
    const ndc = ship.group.getWorldPosition(new THREE.Vector3()).project(camera);
    const c = camera.quaternion;
    const s = ship.group.quaternion;
    const p = gameState().ship.chase.lookPending;
    return {
      cam: { x: c.x, y: c.y, z: c.z, w: c.w },
      ship: { x: s.x, y: s.y, z: s.z, w: s.w },
      offset: {
        x: camera.position.x - ship.group.position.x,
        y: camera.position.y - ship.group.position.y,
        z: camera.position.z - ship.group.position.z,
      },
      shipNdc: { x: ndc.x, y: ndc.y },
      fov: camera.fov,
      aspect: camera.aspect,
      pendingRad: Math.hypot(p.x, p.y, p.z),
      chaseDist: gameState().ship.chase.dist,
    };
  },
  /** Degrees between the camera's forward and the true direction to a row,
   *  the same geometry `cameraPinAim` reads. Null off a row with no live
   *  position. `tools/preview/pin-swing-check.mjs`'s own reading. */
  cameraOffBody(id: string): number | null {
    const at = simBodies?.worldAt(id) ?? null;
    if (at === null) return null;
    const from = simPos.world;
    const dir = new THREE.Vector3(at.x - from.x, at.y - from.y, at.z - from.z);
    if (dir.lengthSq() === 0) return null;
    dir.normalize().applyQuaternion(_invBubble);
    const camFwd = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    return Math.acos(THREE.MathUtils.clamp(dir.dot(camFwd), -1, 1)) * 180 / Math.PI;
  },
  /** Read-only: the row the pin falls back to with no target picked. */
  pinFallbackId(): string | null { return gravityField.frameParentId; },
  velocity(): FlightVec { return { ...flight.velocity }; },
  /** The row the ship is resting on, or null while it is flying. */
  grounded(): string | null { return flight.grounded; },
  /** Speed of the hit that put it there, m/s relative to that row. 0 while flying. */
  get contactSpeedMps(): number { return flight.contactSpeedMps; },
  /** NOSE OFF COURSE's side word for a chosen ship-frame off-axis pair,
   *  carrying the same hold-band state a live reading would. */
  turnSideFromOffAxis(bx: number, by: number): PilotTurnSide | null {
    return pilot.turnSideFromOffAxis(bx, by);
  },
  /** The row the pilot is flying to, or null. */
  target(): string | null { return gameState().ship.flight.flightTargetId; },
  /** Pick one, or clear it. Round 16d's click in the sim view comes in here. */
  setTarget(id: string | null): void { pilot.setFlightTarget(id); },
  /** GO's pick: false, and TARGET unchanged, for a row the list lacks. */
  pickTarget(id: string): boolean {
    if (PICK_UNCHECKED) { pilot.setFlightTarget(id); return true; }
    return pilot.pickTarget(id);
  },
  orientation(): FlightQuat { return { ...flight.orientation }; },
  nose(): FlightVec { return flightNose(flight, { x: 0, y: 0, z: 0 }); },
  /** Degrees between the nose and the velocity at the instant of the boot
   * seed, before any tick has run. Null until `seedShipOrbit` has fired. */
  get bootNoseGapDeg(): number | null { return bootNoseGapDeg; },
  /** Degrees between the nose and the true direction to the focused body at
   * the instant of the boot seed. Null until `seedShipOrbit` has fired. */
  get bootNoseBodyGapDeg(): number | null { return bootNoseBodyGapDeg; },
  hold(a: Partial<FlightAxes & { throttle: number }>): void {
    cruiseDrive.notePilotInput();
    holdFlight({
      pitch: a.pitch ?? 0, yaw: a.yaw ?? 0, roll: a.roll ?? 0, throttle: a.throttle ?? 0,
    });
  },
  release(): void { cruiseDrive.notePilotInput(); holdFlight({ pitch: 0, yaw: 0, roll: 0, throttle: 0 }); },
  clear(): void { cruiseDrive.notePilotInput(); holdFlight(null); },
  /** OWNER DECISION 5: only the map's own panel zeroes the session total. */
  zeroDeltaV(): void { flight.deltaVSpent = 0; },
  // ----- What is pulling, and the rate the pull is integrated at -----
  gravity: FLIGHT_GRAVITY,
  maxSubstepS: FLIGHT_MAX_SUBSTEP_S,
  maxSubsteps: FLIGHT_MAX_SUBSTEPS,
  /** The one clock's rate. There is no second rate on the flight step. */
  get warp(): number { return gameClock().rate; },
  /** The one clock's own epoch, for a check timing a stay the flight step
   *  itself is paused through (a LAND ladder stay does not advance `simSeconds`). */
  get jd(): number { return gameClock().jdTdb(); },
  /** The LAND ladder's own held pin: the body it is measured from, the offset
   *  captured when it opened, and the epoch that offset was captured at. Read,
   *  never set; `tools/preview/grounded-view-check.mjs`'s own PIN check times a
   *  stay off this rather than re-deriving the offset from position samples,
   *  which a frame of drift on either side of the click would move. */
  landPin(): { open: boolean; bodyId: string | null; offset: FlightVec | null; offsetJd: number | null } {
    return { open: landVerb.open, bodyId: landVerb.parentId, offset: landVerb.parentOffset,
      offsetJd: landVerb.parentOffsetJd };
  },
  /**
   * Set the rate and PIN it: no ramp, and no drop takes it away for the rest
   * of the run. A check's own rate control, the same pin `?flightwarp=` sets.
   */
  setWarp(n: number): void {
    warp.pinned = true;
    gameClock().setRate(Number.isFinite(n) && n > 0 ? n : 1);
  },
  /** Sim seconds this step has integrated. What a period is measured against. */
  get simSeconds(): number { return flightSimSeconds; },
  get substeps(): number { return flightLastSubsteps; },
  get substepS(): number { return flightLastSubstepS; },
  /**
   * Arm the flown-path recorder from now: a row every `spacingS` sim seconds of
   * the ship and each of `bodies`, cut at the end of each cruise frame, plus a
   * row at a ground contact's own instant, until `null` disarms it. False on a
   * body the registry lacks or a spacing it cannot step. Costs a few short
   * arrays a frame while armed. `orbit-rings.mjs --trajectory` reads it.
   */
  recordPath(opts: { spacingS: number; bodies: string[] } | null): boolean {
    if (opts === null) {
      pathRecorder.disarm();
      return true;
    }
    flightField.pathIds = [...opts.bodies];
    const v = pathValues();
    return v !== null && pathRecorder.arm(opts.spacingS, flightSimSeconds, v);
  },
  path(): PathReading | null { return pathRecorder.read(); },
  /** The ship's own heliocentric metres. */
  at(): FlightVec {
    const w = shipWorldAt();
    return { x: w.x, y: w.y, z: w.z };
  },
  /** One row's heliocentric metres at this frame's epoch, for a radius vector. */
  bodyAt(id: string): FlightVec | null {
    const w = simBodies?.worldAt(id);
    return w ? { x: w.x, y: w.y, z: w.z } : null;
  },
  /** The ship's own heliocentric metres per second: the flight-frame velocity
   *  with the frame parent's own travel composed back in. The half of SPEED TO
   *  MATCH's truth that is not a registry row's. */
  worldVelocity(): FlightVec {
    const v = shipWorldVelocity();
    return { x: v.x, y: v.y, z: v.z };
  },
  /** One row's own heliocentric metres per second, for its orbital plane. */
  bodyVelocityAt(id: string): FlightVec | null {
    const v = simBodies?.velocityAt(id);
    return v ? { x: v.x, y: v.y, z: v.z } : null;
  },
  /** This tick's pulling set, sorted strongest first. */
  pulls(): Array<{
    id: string; gm: number; distanceM: number; accelMps2: number;
    dx: number; dy: number; dz: number;
  }> {
    readFlightPulls();
    const out: Array<{
      id: string; gm: number; distanceM: number; accelMps2: number;
      dx: number; dy: number; dz: number;
    }> = [];
    for (let i = 0; i < _pulls.length; i++) {
      const p = _pulls[i]!;
      if (!(p.gm > 0)) continue;
      const dx = p.at.x - _field.at.x;
      const dy = p.at.y - _field.at.y;
      const dz = p.at.z - _field.at.z;
      const r = Math.hypot(dx, dy, dz);
      out.push({
        id: _pullIds[i] ?? '', gm: p.gm, distanceM: r,
        accelMps2: r > 0 ? p.gm / (r * r) : 0, dx, dy, dz,
      });
    }
    out.sort((a, b) => b.accelMps2 - a.accelMps2);
    return out;
  },
  /** The pull at the ship, metres per second squared. */
  gravityAccel(): FlightVec {
    readFlightPulls();
    return flightGravity(_field.at, _pulls, { x: 0, y: 0, z: 0 });
  },
  /** Specific potential, joules per kilogram: the sum of -GM/r over the set. */
  potential(): number {
    readFlightPulls();
    return flightPotential(_field.at, _pulls);
  },
  /**
   * Specific orbital energy, `v^2/2 - sum(GM/r)`. The speed is the ship's own
   * parent-relative one and the pulls are this frame's, so a coast with nothing
   * but gravity on it holds this to the tide the moving bodies leave, and the
   * `?drag=1` variant does not.
   */
  energy(): number {
    readFlightPulls();
    const v = flightSpeed(flight);
    return (v * v) / 2 + flightPotential(_field.at, _pulls);
  },
  /** Place the registry at the clock as `load()` does; `save-round-trip.mjs --leg clock-writers --inject place-epoch` is the only caller. */
  placeFrameEpoch(): void { placeFrameEpoch(); },
  // Every verb below writes the state the autopilot is flying, so each takes
  // the stick back off it the way a hand on the controls does.
  /** A circular orbit about a named row at a stated altitude. Not a player's. */
  park(opts: { body: string; altitudeM: number; speedFrac?: number }): ParkResult {
    cruiseDrive.notePilotInput();
    return pilot.parkInOrbit(opts.body, opts.altitudeM, opts.speedFrac ?? 1);
  },
  /** Out along the primary's own line from a row, at a stated speed. Not a player's. */
  parkOutward(opts: { body: string; distanceM: number; speedMps: number }): ParkResult {
    cruiseDrive.notePilotInput();
    return pilot.parkOutwardFrom(opts.body, opts.distanceM, opts.speedMps);
  },
  /** The same drop on a stated line out of a row's own centre rather than out
   *  of its primary's, so a check picks the face it lands on. Not a player's. */
  parkOver(opts: { body: string; distanceM: number; speedMps: number; dir: FlightVec }): ParkResult {
    return pilot.parkOutwardFrom(opts.body, opts.distanceM, opts.speedMps, opts.dir);
  },
  /** Where a resting ship sits against the globe as drawn, or null while flying. */
  groundSeat(): GroundSeatReading | null { return groundSeatReading(); },
  /** The latest contact against the drawn ground, DEV only, or null. */
  lastContact(): ContactReading | null { return flightField.lastContactReading; },
  /** The drawn ground under a direction out of a row's centre and the ship's
   *  own radius from it, flying or at rest, or null while there is no ground to
   *  read. A check's own reading. */
  groundUnder(opts: { body: string; dir?: FlightVec }):
  { shipRadiusM: number; drawnRadiusM: number; contactRadiusM: number | null } | null {
    return groundUnderReading(opts.body, opts.dir ?? null);
  },
  /** `slotSurfaceRadiusM` as the sweep calls it, by the row's own pull slot,
   *  and the direction it handed the Atlas's reader after the spin lead. */
  slotSurface(opts: { body: string; dir: FlightVec; leadS: number }):
  { radiusM: number; readDir: FlightVec; seedJd: number } | null {
    const i = _pullIds.indexOf(opts.body);
    if (i < 0) return null;
    const radiusM = slotSurfaceRadiusM(i, opts.dir.x, opts.dir.y, opts.dir.z, opts.leadS);
    return { radiusM, readDir: { ..._slotSurfaceDir }, seedJd };
  },
  /** At rest, a fraction of the way from one row's centre toward another's. */
  parkBetween(opts: { from: string; toward: string; fraction: number }): ParkBetweenResult {
    cruiseDrive.notePilotInput();
    return pilot.parkBetweenBodies(opts.from, opts.toward, opts.fraction);
  },
  /** On the inbound leg of a named conic about a row. Not a player's. */
  parkApproach(opts: {
    body: string; periapsisM: number; eccentricity: number; trueAnomalyDeg: number;
  }): ParkResult {
    cruiseDrive.notePilotInput();
    return pilot.parkApproachTo(opts.body, opts.periapsisM, opts.eccentricity,
      (opts.trueAnomalyDeg * Math.PI) / 180);
  },
  /** Put the nose and the aim on minus the velocity. A check's own turn. */
  faceRetrograde(): boolean { cruiseDrive.notePilotInput(); return faceRetrogradeNow(); },
  /** A velocity change in the flight frame, m/s: a burn already made, with no
   *  engine time to wait through. The `--transit` arm's own known nudge off a
   *  plan; nothing a player presses reaches it. */
  nudge(opts: { x: number; y: number; z: number }): boolean {
    cruiseDrive.notePilotInput();
    if (!Number.isFinite(opts.x) || !Number.isFinite(opts.y) || !Number.isFinite(opts.z)) {
      return false;
    }
    flight.velocity.x += opts.x;
    flight.velocity.y += opts.y;
    flight.velocity.z += opts.z;
    return true;
  },
};

// The player's ship, its thrusters and the lateral push the flight assist
// commands off them (`src/cruise/playerShip.ts`).
const playerShip = createPlayerShip({
  variant: playerSpec, camera, bubble, flight, flightConfig,
  accelMps2: CRUISE_DRIVE.sustainedMps2,
  assistPushNose: ASSIST_PUSH_NOSE,
});
// `ship` and `thrusters` are faces that outlive a hull swap (`playerShip.swap`),
// so destructuring them bare is safe; the variant is read off the handle.
const {
  ship, thrusters, thrustLateral,
  thrustDrive: _thrustDrive, thrustPush: _thrustPush,
} = playerShip;
scene.userData['simThrusters'] = { read: () => thrusters.read() };

// The ship's place in the bubble's own local metres: the space every entity in
// the fight is placed against. Derived from the doubles like the transforms
// are, never integrated here.
const shipLocal = new THREE.Vector3();
const _toScene = new THREE.Vector3();
const _toBubble = new THREE.Vector3();
const _step = new THREE.Vector3();
const _look = new THREE.Vector3();
/** `seedShipOrbit`'s own scratch for the boot nose reading, taken once at boot. */
const _bootNose: FlightVec = { x: 0, y: 0, z: 0 };

/** A point in the bubble's own metres, in the scene's. */
function bubbleToScene(v: THREE.Vector3): THREE.Vector3 {
  return _toScene.copy(v).applyQuaternion(bubble.quaternion).add(bubble.position);
}

/** A point in the scene's metres, in the bubble's own. */
function sceneToBubble(v: THREE.Vector3): THREE.Vector3 {
  return _toBubble.copy(v).sub(bubble.position).applyQuaternion(_invBubble);
}
const _invBubble = new THREE.Quaternion();

/**
 * The ship's transform and the bubble's, out of the authoritative doubles.
 * Called after anything that moves the ship; nothing else writes either.
 */
function syncShipTransform(): void {
  simPos.localPosition(shipLocal);
  ship.group.position.copy(shipLocal);
  simPos.sceneOffset(bubble.position);
}

/**
 * Take the drawn attitude - the bubble's own local metres - as the flight
 * state's heliocentric one. What a fight's heading, a seed's new basis and a
 * reset all hand over, so the nose never snaps across one of those seams.
 */
function adoptFlightAttitude(): void {
  _flightQuat.copy(bubble.quaternion).multiply(ship.group.quaternion);
  flight.orientation.x = _flightQuat.x;
  flight.orientation.y = _flightQuat.y;
  flight.orientation.z = _flightQuat.z;
  flight.orientation.w = _flightQuat.w;
}

/**
 * Seed the fight around the ship where it stands, with the ship at `local` in
 * the fight's own metres.
 *
 * A scenario opened or restarted puts the ship where it is now, riding its
 * parent (Erik, 2026-09-26; every scenario open, 2026-09-27): the heliocentric
 * point does not move, and the bubble's zero and axes are taken again around
 * it so the scenario's layout, written ship-relative, lands around the ship
 * rather than around wherever the bubble was last seeded. It used to drop the
 * ship back on that old zero, which cruise does not carry, so after a LAUNCH
 * or any travel the ship jumped to a point its parent had long since left. The
 * axes are the ship's orbital frame about its parent, as a seed's are; a state
 * with no plane of its own (no parent, or at rest against it) keeps the axes
 * it has.
 */
function seedFightHere(local: THREE.Vector3): void {
  if (RESTART_NOSEED) return;
  if (RESTART_ANCHOR) {
    simPos.setLocal(local);
    syncShipTransform();
    return;
  }
  const w = simPos.world;
  const parentId = gravityField.frameParentId;
  const at = parentId === null ? null : simBodies?.worldAt(parentId) ?? null;
  let basis: Basis = simPos.basis;
  if (at !== null) {
    try {
      basis = orbitalBasis({ x: w.x - at.x, y: w.y - at.y, z: w.z - at.z }, flight.velocity);
    } catch {
      // Degenerate: the state names no plane, so the fight stays on this one.
    }
  }
  simPos.restoreFrame(w, {
    x: w.x - (basis.x.x * local.x + basis.y.x * local.y + basis.z.x * local.z),
    y: w.y - (basis.x.y * local.x + basis.y.y * local.y + basis.z.y * local.z),
    z: w.z - (basis.x.z * local.x + basis.y.z * local.y + basis.z.z * local.z),
  }, basis);
  // The ship is at `local` exactly, held in the fight's own metres rather than
  // read back out of the heliocentric doubles it was just placed from, so every
  // scenario opens on the same low bits wherever in the solar system it opens.
  simPos.setLocal(local);
  syncBubbleFrame();
  syncShipTransform();
}

/** The bubble's own axes, off the ship's orbital frame. Only a seed moves it. */
function syncBubbleFrame(): void {
  const b = simPos.basis;
  _basisMat.makeBasis(
    new THREE.Vector3(b.x.x, b.x.y, b.x.z),
    new THREE.Vector3(b.y.x, b.y.y, b.y.z),
    new THREE.Vector3(b.z.x, b.z.y, b.z.z),
  );
  bubble.quaternion.setFromRotationMatrix(_basisMat);
  _invBubble.copy(bubble.quaternion).invert();
  // The chase camera's roll: `lookAt` reads `up` in world space, so the fight's
  // own up has to be handed over in world axes or the frame rolls with the
  // ecliptic instead of with the plane the ship is flying in.
  camera.up.set(b.y.x, b.y.y, b.y.z);
  headingPlane.normal.set(b.y.x, b.y.y, b.y.z);
}
syncShipTransform();

// ---------- Camera rig ----------
// The chase rig, the ground stand-off camera and the body pin
// (src/cruise/cameraRig.ts). Built here, once the ship and the bubble's
// inverse both exist; `updatePinButton` reaches the HUD action bar built below,
// and `autopilotFlying` the cruise drive built after it.
const rig = createCameraRig({
  camera, renderer, ship, flight, simPos, gravityField, atlasWorld,
  invBubble: _invBubble,
  live: {
    get simBodies() { return simBodies; },
    get bodyRows() { return bodyRows; },
  },
  updatePinButton: () => hudButtons.updatePinButton(),
  autopilotFlying: () => cruiseDrive.autopilot !== null,
  hullRadius: () => playerShip.hullRadius(),
});

// ---------- Where the ship is going ----------
const progradeHud = createProgradeView({
  camera, scene, bubble, invBubble: _invBubble, flight,
  // `pilot` is built later, right after `cruiseDrive` - the same
  // forward-reference-inside-a-closure pattern `rig`'s own
  // `updatePinButton: () => hudButtons.updatePinButton()` already uses for
  // `hudButtons`, which is also built later. Never evaluated until the frame
  // loop calls it, long after this module's top level has finished running.
  guidanceToTarget: () => pilot.guidanceToTarget(),
  assistLive,
  targetNamed: () => {
    const id = gameState().ship.flight.flightTargetId;
    return id !== null && bodyLabels.shows(id);
  },
  get engagement() { return game.engagement; },
  get dvCorrection() { return dvCorrection; },
});

// ---------- Input ----------
const input = createInput();

/** The ASSIST button's press. */
function toggleAssist(): void {
  // The switch is a flight control: it takes the stick back off the delta-v
  // map's autopilot, which flies with the assist off and restores it.
  cruiseDrive.notePilotInput();
  const turningOn = !gameState().ship.flight.assistOn;
  setAssistOn(turningOn);
  // What the hand-back restores, so a switch thrown mid-flight is the state
  // the player gets back rather than the one they had before GO.
  cruiseDrive.autopilotAssistWas = turningOn;
  // Inert on the shipped path, where a gap closes with no axis held anyway.
  // It is what `?assistheldonly=1` leaves as the one no-axis case, so the
  // `--assist --inject assist-held-only` control still reads an OFF->ON edge
  // the way a player makes it (`applyAssist`, src/sim/flight.ts).
  // The switch is not an aim. It arms a catch-up toward the aim the player
  // already commanded, and where none has been commanded it closes nothing,
  // so an off-and-on click at boot costs what the boot costs.
  if (turningOn) {
    if (ASSIST_AIM_FROM_NOSE) aimAlongNose(flight);
    if (!ASSIST_NORESYNC && flight.assistAim !== null) flight.assistCatchUp = true;
  } else flight.assistCatchUp = false;
}
const pilotPanel = createCruisePilotPanel({
  get trajectory() { return trajectory; },
  onSolarSystem: () => { openMenuTab('solar'); },
});

// ---------- The land verb ----------
//
// `src/sim/landVerb.ts` owns the verb: the Atlas host mounted at boot, the
// camera handover, the landed pin and the way back out. What stays here are the
// two accessors for the handles that mount publishes on the shared scene.
const landVerb = createCruiseLandVerb({
  simPos, flight, simWorld, attemptLaunch, atlasCamera, atlasWorld,
  // DEV `?ground=explore` mounts explore's ground game on play's page (tools/preview/atlas-ground-check.mjs).
  atlasGround: ground,
  get simBodies() { return simBodies; },
  get bodyRows() { return bodyRows; },
  get frameParentId() { return gravityField.frameParentId; },
  get seedJd() { return seedJd; },
  get hudButtons() { return hudButtons; },
});

function atlasCamera(): AtlasCameraHandle | null {
  return (scene.userData['atlasCamera'] as AtlasCameraHandle | undefined) ?? null;
}

function atlasWorld(): AtlasWorldHandle | null {
  return (scene.userData['atlasWorld'] as AtlasWorldHandle | undefined) ?? null;
}
// The mount activated its own subtree; cruise is the sim drawing with the
// Atlas's world co-attached under the same root.
atlasWorld()?.setExternal(true);
simWorld.activate();

// ---------- The ship's own predicted path ----------
//
// Off until the pilot asks for it. `src/sim/trajectory.ts` owns the prediction
// and the line; `cruise/verbs.ts` owns the switch, the clock, the DEV flags
// and the pulling set the prediction is handed.
const { trajectory, sampleTrajectoryBody, syncTrajectoryCaption } = createCruiseTrajectory({
  flightConfig, flight, scene, atlasWorld,
  pullRows: _pullRows,
  get _pulls() { return _pulls; },
  get simBodies() { return simBodies; },
  get bodyRows() { return bodyRows; },
  get seedJd() { return seedJd; },
});

input.on('land', () => landVerb.attempt());
input.on('launch', attemptLaunch);

// ---------- Time warp: how fast the trip plays is the player's ----------
//
// `src/sim/warp.ts` owns the warp control, the automatic drops and the readout;
// `src/world/clock.ts` holds the levels and the ramp. `cruise/verbs.ts` owns
// the state warp reads live off the rest of the file and the debug bus
// `tools/preview/descent-dojo.mjs --warp` reads off the scene.
const { warp } = createCruiseWarp({
  input, ship, vitals, simPos, scene, CLOCK_CONST,
  pickDominantPull, shipWorldAt, shipWorldVelocity, readFlightPulls,
  requestAutopilotWarp: (rate, ceiling) => cruiseDrive.requestAutopilotWarp(rate, ceiling),
  notePilotInput: () => cruiseDrive.notePilotInput(),
  get engagement() { return game.engagement; },
  get autopilot() { return cruiseDriveBuilt?.autopilot ?? null; },
  get autopilotCmd() { return cruiseDriveBuilt?.autopilotCmd ?? null; },
  get autopilotWarpWish() { return cruiseDriveBuilt?.autopilotWarpWish ?? null; },
  set autopilotWarpWish(v: number | null) { if (cruiseDriveBuilt) cruiseDriveBuilt.autopilotWarpWish = v; },
  get _pulls() { return _pulls; },
  get _pullIds() { return _pullIds; },
  get bodyRows() { return bodyRows; },
  get simBodies() { return simBodies; },
  get flightSimSeconds() { return flightSimSeconds; },
  BODIES_PINNED, FLIGHT_CARRY_OLD_PARENT, FLIGHT_CENTRES_HELD, MARKER_STALE, LINE_STALE,
});

// The plane a click sets a heading on is the plane the ship is already flying
// in: its normal is the bubble's own +y, which `syncBubbleFrame` writes from
// the orbital normal of the ship's heliocentric state. World Y until the
// registry lands and the first seed tilts it.
const headingPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

// ---------- The HUD action buttons ----------
// The cruise set, then the game's own added to it.
const hudButtons = createHudButtons({
  input, toggleAssist, atlasCamera,
  recentreChase: () => rig.recentreChase(),
  resetChase: () => rig.resetChase(),
  setCameraPin: (on) => rig.setCameraPin(on),
  chaseOffNose: () => rig.chaseOffNose(),
  chaseOffDefault: () => rig.chaseOffDefault(),
  cameraPinBodyId: () => rig.cameraPinBodyId(),
  assist: () => ({ on: gameState().ship.flight.assistOn, live: assistLive() }),
  attemptLand: () => landVerb.attempt(),
  landTarget: () => landVerb.target(),
  namesOn: () => bodyLabels.isOn(),
  setNames: (on, source) => bodyLabels.setOn(on, source),
  inFight: () => game.engagement !== null,
  get cameraPinOn() { return rig.cameraPinOn; },
  get landOpen() { return landVerb.open; },
  get bodyRows() { return bodyRows; },
});

// ---------- Post-processing ----------
const postFx = createPostFx({ renderer, scene, camera });

// Cruise's drive and GO's autopilot, the delta-v map and its panel. The flight
// field and the ship's place stay here for now and go in whole.
const cruise = createCruiseSim({
  scene, input, ship, thrusters, flight, flightConfig, simPos, gravityField, trajectory,
  pathRecorder, warp, driveLimits,
  inFight: () => game.engagement !== null,
  deployForTrip, tripThrustShare, beforeFly,
  selectTarget: (id) => pilot.pickTarget(id),
  rebaseFlightFrame, advanceSimBodies, frameParentStep, stepFlightFrame, noteGroundContact,
  noteStopLateSag, notePathRow, assistSeconds, assistLive, thrustLateral, syncShipTransform,
  shipWorldAt, shipWorldVelocity, sampleTrajectoryBody, atlasWorld,
  _frameCtx, _step, _thrustDrive, _thrustPush, _invBubble, _flightQuat,
  FLIGHT_CARRY_OLD_PARENT, BODIES_PINNED, AUTOPILOT_KEEP_AIM, MAX_REAL_DT_S,
  live: {
    get simBodies() { return simBodies; },
    get bodyRows() { return bodyRows; },
    get seedJd() { return seedJd; },
    get flightSimSeconds() { return flightSimSeconds; },
    set flightSimSeconds(v: number) { flightSimSeconds = v; },
    get flightLastSubsteps() { return flightLastSubsteps; },
    set flightLastSubsteps(v: number) { flightLastSubsteps = v; },
    get flightLastSubstepS() { return flightLastSubstepS; },
    set flightLastSubstepS(v: number) { flightLastSubstepS = v; },
  },
});
const cruiseDrive = cruise.drive;
cruiseDriveBuilt = cruiseDrive;
const { dvMap, dvMapPanel, dvCorrection } = cruiseDrive;

// ---------- The body the pilot is flying to ----------
// The target pick, its guidance, the pilot panel's own readout and the park
// verbs (src/cruise/pilotTarget.ts). Built here rather than beside `rig`
// above: `computePilotReadout` needs `cruiseDrive.throttleApplied`, which does
// not exist until this line.
const pilot = createPilotTarget({
  simPos, flight, gravityField, ship, atlasWorld,
  invBubble: _invBubble,
  warp, trajectory, cruiseDrive, syncBubbleFrame, syncShipTransform, adoptFlightFrame,
  contactRadiusM, assistLive, flightConfig,
  live: {
    get simBodies() { return simBodies; },
    get bodyRows() { return bodyRows; },
  },
});

// M is a shortcut for the panel's own minimize button, not a second way to
// show and hide it: the panel is in the rail from boot like every other one.
input.on('dvMap', () => { if (!game.engagement) toggleHudPanel('dv-map'); });

// NAMES: the bodies' names over the view. A click picks the delta-v map's
// trip and nothing else; the pilot target and the pin stay with their own
// controls. On by default; the switch is remembered.
const labelParams = typeof location !== 'undefined' ? new URLSearchParams(location.search) : null;
const labelFlag = (k: string): boolean => TEST_SWITCHES && labelParams?.get(k) === '1';
const _labelCamScene = new THREE.Vector3();
const bodyLabels = createBodyLabels({
  camera,
  invBubble: _invBubble,
  cameraWorld: (out) => {
    camera.updateWorldMatrix(true, false);
    camera.getWorldPosition(_labelCamScene);
    return simPos.cameraWorld(_labelCamScene, out);
  },
  places: () => simBodies?.places ?? [],
  frameParentId: () => gravityField.frameParentId,
  targetId: () => gameState().ship.flight.flightTargetId,
  pinBodyId: () => (rig.cameraPinOn ? rig.cameraPinBodyId() : null),
  dvMap,
  revealDvMap: () => {
    if (document.querySelector('[data-widget="dv-map"]')?.classList.contains('hud-minimized')) {
      toggleHudPanel('dv-map');
    }
  },
  host: app,
  defaultOn: true,
  inject: {
    clickInert: labelFlag('labelclickinert'),
    styleFlat: labelFlag('labelstyleflat'),
    localAll: labelFlag('labellocalall'),
    noDeclutter: labelFlag('labelnodeclutter'),
    noPersist: labelFlag('labelnopersist'),
  },
});
// Read by tools/preview/body-labels-check.mjs.
scene.userData['simBodyLabels'] = {
  state: () => bodyLabels.state(),
  isOn: () => bodyLabels.isOn(),
};

// ---------- Perf detail panel (lives in the DEV cluster) ----------
const perfPanel = createPerfPanel({ renderer });

const menuSystems = createCruiseMenuSystems({
  inFight: () => game.engagement !== null,
  clock, flight, flightConfig, simPos, gravityField, trajectory, landVerb,
  rebaseFlightFrame, advanceSimBodies, frameParentStep, stepFlightFrame, noteGroundContact,
  shipWorldAt, sampleTrajectoryBody, syncTrajectoryCaption, _step,
  FLIGHT_CARRY_OLD_PARENT, BODIES_PINNED, MARKER_STALE, LINE_STALE,
  live: {
    get simBodies() { return simBodies; },
    get bodyRows() { return bodyRows; },
    get seedJd() { return seedJd; },
    get flightSimSeconds() { return flightSimSeconds; },
    set flightSimSeconds(v: number) { flightSimSeconds = v; },
    get flightLastSubsteps() { return flightLastSubsteps; },
    set flightLastSubsteps(v: number) { flightLastSubsteps = v; },
    get flightLastSubstepS() { return flightLastSubstepS; },
    set flightLastSubstepS(v: number) { flightLastSubstepS = v; },
  },
});

const viewSystems = createCruiseViewSystems({
  inFight, fightCamera, rebaseInFight, sceneMisc, hud: hudSeam,
  input, rig, bodyLabels, camera, simPos, syncShipTransform, progradeHud, worldFrame,
  atlasWorld, warp, syncTrajectoryCaption, simWorld, world, postFx, pilotPanel, pilot,
  trajectory, hudButtons, cruiseDrive, dvMap, dvMapPanel, dvCorrection,
  _cameraScene, _cameraWorld,
  live: {
    get simBodies() { return simBodies; },
    get seedJd() { return seedJd; },
  },
});

/**
 * `?simorrery=1`: the bug put back on purpose.
 *
 * One flat, unlit sphere per row at its own heliocentric place, registered in
 * the SIM's own world frame (not the Atlas's) so it draws over the Atlas's
 * globe at the same place and the same size - `tools/preview/descent-dojo.mjs
 * --world --inject sim-orrery` needs the dominant body to carry a second mesh
 * tagged with its own `bodyId`, whose material is not imagery-textured, for
 * the material read to have something to fail on.
 */
function mountSimOrreryDebug(places: BodyPlaces): void {
  const colour = new THREE.Color();
  for (const place of places.places) {
    colour.set(place.row.albedo_color);
    const mesh = new THREE.Mesh(
      new THREE.SphereGeometry(place.row.radius_m, 24, 16),
      new THREE.MeshBasicMaterial({ color: colour.clone() }),
    );
    mesh.name = `orrery-${place.row.id}`;
    mesh.userData['bodyId'] = place.row.id;
    worldFrame.register(mesh, () => place.world);
  }
}

// ---------- The scenario's own orbit ----------
// OWNER DECISION 2: a scenario places the SHIP, as an orbit, and nothing else.
// The registry arrives over the network, so the bubble is seeded onto that
// orbit's propagated state as soon as it does; until then the fight sits at the
// frame origin, which is where it sat before it had a world position at all.
function seedShipOrbit(rows: readonly import('../world/types').BodyRow[]): void {
  const spec = shipOrbitById(ORBIT_ID);
  if (!spec) {
    console.warn(`ship orbit "${ORBIT_ID}" is not one this build carries; staying at the frame origin`);
    return;
  }
  const parent = bodyById(rows, spec.parent);
  if (!parent) {
    console.warn(`ship orbit "${spec.id}" is relative to "${spec.parent}", which the registry has no row for`);
    return;
  }
  const elements = shipOrbitElements(spec, parent);
  const jd = gameClock().jdTdb();
  seedJd = jd;
  seedJdPrev = jd;
  bootJd = jd;
  const state = heliocentricOrbit(elements, spec.parent, rows, jd);
  const r = { x: state.position.x * 1000, y: state.position.y * 1000, z: state.position.z * 1000 };
  const v = { x: state.velocity.x * 1000, y: state.velocity.y * 1000, z: state.velocity.z * 1000 };
  const relState = stateAt(elements, jd).velocity;
  const rel = { x: relState.x * 1000, y: relState.y * 1000, z: relState.z * 1000 };
  // The orbit is the ship's MOTION as well as its place, and the frame that
  // motion is in is the PARENT. The velocity the flight state wants - and the
  // attitude basis has to agree with - is the parent-relative half of the
  // state, not the composed heliocentric one. That was true when the registry
  // was pinned because a row was then at rest; it is true now because an orbit
  // is only an orbit in the frame of the body it is about.
  // Mars's own ~24 km/s heliocentric velocity sits nearly orthogonal to a
  // ~1.4 km/s areostationary orbit, so a basis built from the composed
  // velocity put the ship's nose 85 degrees off the velocity it actually flew
  // with, and the assist spent 1,200 m/s in the first minute closing that gap.
  simPos.seedAt(r, orbitalBasis(r, BOOT_HELIO_BASIS ? v : rel));
  // Without this the ship boots at rest 17,000 km up and falls into its own
  // parent the moment the clock runs fast.
  if (!BOOT_REST) {
    flight.velocity.x = rel.x;
    flight.velocity.y = rel.y;
    flight.velocity.z = rel.z;
  }
  adoptFlightFrame(spec.parent);
  flightField.seedOnFit = !NO_SEED_CARRY && parent.orbit?.osculating !== undefined
      && parent.orbit.sets === undefined
    ? { parentId: parent.id, dvSpent: flight.deltaVSpent }
    : null;
  syncBubbleFrame();
  syncShipTransform();
  simBodies = createBodyPlaces({ rows, unlatched: landLabelUnlatched() });
  simBodies.update(jd, simPos.world);
  if (SIM_ORRERY) mountSimOrreryDebug(simBodies);
  // OWNER DECISION 2's own sentence: the body the ship is orbiting is a bright
  // disc OFF THE BOW. Which way that is follows from the orbit, so the bow is
  // pointed at it rather than the orbit being chosen to suit a fixed heading.
  const focused = simBodies.focus();
  let toBody: WorldVec | null = null;
  // The registry lands on a promise, so this runs AFTER the boot scenario has
  // applied and the bow swing below overwrites whatever heading that scenario
  // set. Harmless for a scenario whose contacts surround the player; fatal for
  // the SFX range, whose whole geometry is a lane laid down one specific
  // bearing. That scenario aims its own bow, so it opts out of the framing.
  if (focused && bowFramed()) {
    const at = simBodies.worldAt(focused.row.id);
    if (at) {
      toBody = { x: at.x - r.x, y: at.y - r.y, z: at.z - r.z };
      simPos.unrotate(toBody, _look);
      const heading = Math.atan2(-_look.x, -_look.z);
      setBootHeading(heading);
      ship.group.rotation.y = heading;
    }
  }
  // The seed turned the bubble; the drawn bow is what the flight state means.
  adoptFlightAttitude();
  // The bow points at the body for framing (OWNER DECISION 2), which a
  // circular orbit's velocity is perpendicular to, so this attitude is not an
  // aim anybody asked the assist to fly onto. The boot aim is the ship's own
  // velocity - no gap and no spend - which is what a null aim means; nothing
  // is commanded until the player turns or a park puts the nose prograde.
  flight.assistAim = null;
  if (ASSIST_BOOT_ARMED) aimAlongNose(flight);
  // Taken here, before a single tick has run: the assist has had zero real
  // time to close any gap, so this is the boot state itself and not a poll
  // that caught it already correcting.
  {
    const nose = flightNose(flight, _bootNose);
    const speed = flightSpeed(flight);
    bootNoseGapDeg = speed > 0
      ? Math.acos(THREE.MathUtils.clamp(
        (nose.x * flight.velocity.x + nose.y * flight.velocity.y + nose.z * flight.velocity.z) / speed,
        -1, 1,
      )) * 180 / Math.PI
      : null;
    if (toBody) {
      const bl = Math.hypot(toBody.x, toBody.y, toBody.z);
      bootNoseBodyGapDeg = bl > 0
        ? Math.acos(THREE.MathUtils.clamp(
          (nose.x * toBody.x + nose.y * toBody.y + nose.z * toBody.z) / bl, -1, 1,
        )) * 180 / Math.PI
        : null;
    } else {
      bootNoseBodyGapDeg = null;
    }
  }
  // What `tools/preview/descent-dojo.mjs --ship` reads the bubble back by.
  scene.userData['simBubble'] = {
    orbitId: spec.id,
    parentId: spec.parent,
    seedJd: jd,
    fieldRadius: traffic.fieldRadius,
    headingNormal: (): WorldVec => ({
      x: headingPlane.normal.x, y: headingPlane.normal.y, z: headingPlane.normal.z }),
    ships: (): Array<{ name: string; canonical: boolean; range: number }> => traffic.ships(),
    /**
     * The ship's offset from the body its own orbit is about, in heliocentric
     * metres.
     *
     * The land bar reads this rather than the heliocentric position. The one
     * clock runs while the Atlas has the camera, so the ship carries with its
     * parent over a landing on purpose; what a landing must not move is its
     * place in the parent's frame.
     */
    parentOffset: (): WorldVec | null => {
      const at = simBodies?.worldAt(spec.parent);
      if (!at) return null;
      const w = simPos.world;
      return { x: w.x - at.x, y: w.y - at.y, z: w.z - at.z };
    },
    focus: (): { id: string; radiusM: number; distanceM: number } | null => {
      const f = simBodies?.focus();
      return f ? { id: f.row.id, radiusM: f.row.radius_m, distanceM: f.distanceM } : null;
    },
  };
}

function boot(): void {
void loadBodies()
  .then(async (rows) => {
    bodyRows = rows;
    // The orbit is about where the parent's own sets put it, not its secular fit.
    // One try only: boot waits for a deferred parent's first answer, however
    // slow and whoever asked for it, but not for any retry after a dropped
    // one; the ship then seeds from the secular fit, and a retry that lands
    // later carries it (`carrySeedOntoLateSets`).
    const parentId = shipOrbitById(ORBIT_ID)?.parent;
    if (parentId !== undefined && !SEED_BEFORE_SETS) {
      await ensureOsculating(rows, [parentId], 1).catch((err: unknown) => {
        console.error(`the osculating sets for "${parentId}" did not attach, so the ship is seeded `
          + 'from its secular fit and starts off its stand', err);
      });
    }
    seedShipOrbit(rows);
    pilot.setFlightTarget(defaultFlightTargetId(rows));
    pilot.rebuildTargetOptions();
  })
  .catch((err: unknown) => {
    console.warn('the body registry did not load, so the ship keeps the frame origin', err);
  });
}

return {
  world, scene, simWorld, simRoot, camera, worldFrame, bubble, simLights, simPos,
  flightField, flight, flightConfig, gravityField, pathRecorder,
  rideFightFrame, placeFrameEpoch, adoptFlightFrame, shipWorldAt, shipWorldVelocity,
  _ground, _pulls, _pullIds, _pullRadii, _pullRows, defaultFlightTargetId,
  ship, thrusters, playerShip, shipLocal, bubbleToScene, sceneToBubble,
  syncShipTransform, adoptFlightAttitude, syncBubbleFrame, seedFightHere,
  rig, input, pilotPanel, landVerb, trajectory, warp, headingPlane, hudButtons, postFx,
  cruiseDrive, dvMap, pilot, bodyLabels, perfPanel,
  // Scratch the game's own code in main.ts shares.
  _look, _toBubble, _step, _thrustDrive,
  get bodyRows() { return bodyRows; },
  /** Put `?date=` back on the clock; a boot scenario's state load writes the anchor over the factory's own read. */
  applyBootDate(): void { if (DATE_PARAM !== null) setDate(DATE_PARAM); },
  systems: { live: cruise.systems, menu: menuSystems, view: viewSystems },
  boot,
  dispose: () => cruise.dispose(),
};
}
